import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { take, tap } from 'rxjs/operators';
import { UserSettingsStore } from '@mhe/reader/features/user-settings';
import { ReaderConfigStore } from '@mhe/reader/components/reader/state/reader-config.store';
import { ReaderStore } from '@mhe/reader/components/reader/state/reader.store';
import * as userSettingsActions from '@mhe/reader/features/user-settings/state/user-settings.actions';
import { TranslationToolLoaderService } from '@mhe/reader/features/translation-tool/translation-tool-loader.service';
import { ReaderTokenService } from '../../../tokens/reader-token.service';

@Component({
  selector: 'rdrx-translation-tool-shell',
  templateUrl: './translation-tool-shell.component.html',
  styleUrls: ['./translation-tool-shell.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TranslationToolShellComponent implements OnInit {
  private readonly userSettingsStore = inject(UserSettingsStore);
  private readonly readerConfigStore = inject(ReaderConfigStore);
  private readonly readerStore = inject(ReaderStore);
  private readonly readerTokenService = inject(ReaderTokenService);

  characterLimit = signal<number | undefined>(undefined);
  targetLanguage = signal<string>('');
  environment = signal<string | undefined>(undefined);
  sourceLanguage = signal<string>('');
  contextId = signal<string | undefined>(undefined);
  platform = signal<string | undefined>(undefined);
  orgXid = signal<string | undefined>(undefined);
  personXid = signal<string | undefined>(undefined);
  sectionXid = signal<string | undefined>(undefined);
  pageId = signal<string | undefined>(undefined);
  chapterId = signal<string | undefined>(undefined);
  epubRelease = signal<string | undefined>(undefined);
  epubReleaseVersion = signal<string | undefined>(undefined);
  authToken = toSignal(this.readerTokenService.getToken(), { initialValue: undefined });

  readonly selectedText = input.required<string>();
  readonly closeRequested = output();

  translationReady = false;

  constructor(
    private readonly translationLoader: TranslationToolLoaderService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.translationLoader.loadTranslationTool().subscribe({
      next: () => {
        this.translationReady = true;
        this.cdr.markForCheck();
      },
      error: (err) => console.error('Failed to load translation tool:', err),
    });

    this.readerConfigStore.environment$
      .pipe(take(1))
      .subscribe((env) => {
        this.environment.set(env?.sdlc ?? 'local');
      });

    // Get contextId from ReaderConfigStore
    this.readerConfigStore.contextId$
      .pipe(take(1))
      .subscribe((contextId) => {
        this.contextId.set(contextId || undefined);
      });

    // Get platform from ReaderConfigStore
    this.readerConfigStore.platform$
      .pipe(take(1))
      .subscribe((platform) => {
        this.platform.set(platform || undefined);
      });

    // Get epub release and version from ReaderConfigStore
    this.readerConfigStore.epubReleaseUUID$
      .pipe(take(1))
      .subscribe((epubReleaseUUID) => {
        this.epubRelease.set(epubReleaseUUID || undefined);
      });

    this.readerConfigStore.epubVersion$
      .pipe(take(1))
      .subscribe((epubVersion) => {
        this.epubReleaseVersion.set(epubVersion || undefined);
      });

    // Get orgXID and personXID from ReaderConfigStore runtime$
    this.readerConfigStore.runtime$
      .pipe(take(1))
      .subscribe((runtime) => {
        this.orgXid.set(runtime.orgXid || undefined);
        this.personXid.set(runtime.personXid || undefined);
        this.sectionXid.set(runtime.sectionXid || undefined);
      });

    // Get pageID from current spine item
    this.readerStore.spineItem$
      .pipe(take(1))
      .subscribe((spineItem) => {
        if (spineItem?.id) {
          this.pageId.set(spineItem.id);
          // Also try to get chapter ID from the spine position
          if (spineItem.index !== undefined) {
            this.chapterId.set(`${spineItem.index}`);
          }
        }
      });

    // Get contextId from the reader config store
    this.readerConfigStore.contextId$
      .pipe(take(1))
      .subscribe((contextId) => {
        this.contextId.set(contextId);
      });

    // Prefer runtime locale (set when EPUB is loaded) as the source language
    this.readerConfigStore.epubLanguageLocale$.pipe(take(1)).subscribe((runtimeLanguageLocale) => {
      if (runtimeLanguageLocale) {
        this.sourceLanguage.set(runtimeLanguageLocale.split(/[-_]/)[0].toLowerCase());
        return;
      }
      // If no runtime locale is available default to 'en'
      this.sourceLanguage.set('en');
    });

    this.userSettingsStore.translationTargetLanguage$
      .pipe(take(1))
      .subscribe((currentTargetLanguage) =>
        this.targetLanguage.set(currentTargetLanguage ?? ''),
      );
  }

  protected handleClose(): void {
    this.closeRequested.emit();
  }

  protected handleTargetLanguageChange(event: CustomEvent<string>): void {
    const lang = event?.detail;

    this.targetLanguage.set(lang);
    this.userSettingsStore.setUserSettings({ translationTargetLanguage: lang });
    this.userSettingsStore.dispatch(
      userSettingsActions.setUserSettings({
        userSettings: { translationTargetLanguage: lang },
      }),
    );
  }

  protected handleTranslationError(event: CustomEvent): void {
    const error = event?.detail;

    if (error?.code === 'AUTH_UNAUTHORIZED' || error?.status === 401) {
      // Refresh token and update store
      this.readerTokenService
        .refreshToken()
        .pipe(tap((session) => {
          this.readerTokenService.setToken(session);
        }))
        .subscribe();
    } else {
      // Log non-401 errors to console
      console.error('Translation Web Component Error:', error?.message, error?.details || error);
    }
  }
}
